import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Layout from './Layout';
import * as XLSX from 'xlsx';
import {
    Button,
    Card,
    Checkbox,
    Col,
    Collapse,
    Descriptions,
    Empty,
    Image,
    Input,
    Modal,
    Radio,
    Row,
    Select,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Typography,
    Upload,
    Alert,
    message
} from 'antd';
import { UploadOutlined, SearchOutlined, CheckOutlined, LinkOutlined, ExportOutlined } from '@ant-design/icons';
import '../styles/CustomerCabinetPage.css';
import {
    resizableTableComponents,
    useResizableColumns,
    useUserUiSetting
} from '../utils/resizableColumns';

const { Title, Text } = Typography;
const { TextArea } = Input;

const KIND_LABELS = {
    matched: ['совпало', 'green'],
    qty_info: ['кол-во разное · сумма ок', 'blue'],
    mismatch: ['расхождение', 'orange'],
    missing_in_report: ['есть в заказе СКС · нет в отчёте Т2', 'red'],
    extra_in_report: ['есть в отчёте Т2 · нет в заказе СКС', 'purple']
};

const kindTag = kind => {
    const [text, color] = KIND_LABELS[kind] || [kind, 'default'];
    return <Tag color={color}>{text}</Tag>;
};

const kindPlain = kind => KIND_LABELS[kind]?.[0] || kind;

const roundQty = n =>
    Math.round((Number(n) || 0) * 1000) / 1000;

const roundMoney = n =>
    Math.round((Number(n) || 0) * 100) / 100;

const money = n =>
    roundMoney(n).toLocaleString('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

/** Порог для массового согласования мелких расхождений */
const SMALL_DISCREPANCY_LIMIT = 100;

/**
 * Мелкие: «нет в отчёте» / «нет в заказе» и Δ суммы (обе стороны есть),
 * если |Δ| ≤ лимита — в т.ч. расхождения на копейки.
 * qty_info / matched не трогаем.
 */
const isSmallSideDiscrepancy = row => {
    if (!row) return false;
    if (
        row.kind !== 'missing_in_report' &&
        row.kind !== 'extra_in_report' &&
        row.kind !== 'mismatch'
    ) {
        return false;
    }
    const amount = Math.abs(Number(row.amountDelta) || 0);
    return amount > 0 && amount <= SMALL_DISCREPANCY_LIMIT;
};

/** Позиция без расхождения по сумме (включая разное кол-во при той же сумме) */
const isSumOkKind = kind => kind === 'matched' || kind === 'qty_info';

const normalizeWorkCode = code => {
    const s = String(code || '').trim();
    if (!s) return '';
    return s.endsWith('.') ? s : `${s}.`;
};

const isExcludedWorkCode = code =>
    normalizeWorkCode(code).startsWith('13.');

const splitWorkName = value => {
    const source = String(value || '').trim();
    if (!source) return { code: '', name: '' };
    const match = source.match(/^(\d+(?:\.\d+)*\.?)\s*(.*)$/);
    if (!match) return { code: '', name: source };
    let code = match[1] || '';
    const name = String(match[2] || '').trim();
    if (code && !code.endsWith('.')) code += '.';
    return { code, name };
};

const normalizeTaskNumber = value =>
    String(value || '')
        .trim()
        .replace(/\s+/g, '');

const taskGroupKey = row => {
    const ttRaw =
        row.claimNumber ||
        row.taskNumber ||
        row.woNumber ||
        (row.taskId ? `ID ${row.taskId}` : '');
    return (
        normalizeTaskNumber(ttRaw).toUpperCase() ||
        `TASK:${row.taskId || row.key}`
    );
};

/** Группы строк сверки по заявке (ТТ) */
const buildTaskGroups = rows => {
    const map = new Map();
    (rows || []).forEach(row => {
        const gKey = taskGroupKey(row);
        if (!map.has(gKey)) {
            map.set(gKey, {
                key: gKey,
                ttLabel:
                    row.claimNumber ||
                    row.taskNumber ||
                    row.woNumber ||
                    (row.taskId ? `ID ${row.taskId}` : '—'),
                bsNumber: row.bsNumber || '',
                taskId: row.taskId || null,
                rows: [],
                sumExpected: 0,
                sumReport: 0,
                sumDelta: 0,
                mismatchCount: 0,
                matchedCount: 0,
                qtyInfoCount: 0
            });
        }
        const group = map.get(gKey);
        if (row.bsNumber && !group.bsNumber) group.bsNumber = row.bsNumber;
        if (row.taskId && !group.taskId) group.taskId = row.taskId;
        group.rows.push(row);
        group.sumExpected = roundMoney(
            group.sumExpected + (Number(row.expectedAmount) || 0)
        );
        group.sumReport = roundMoney(
            group.sumReport + (Number(row.reportAmount) || 0)
        );
        group.sumDelta = roundMoney(
            group.sumDelta + (Number(row.amountDelta) || 0)
        );
        if (row.kind === 'qty_info') group.qtyInfoCount += 1;
        else if (isSumOkKind(row.kind)) group.matchedCount += 1;
        else group.mismatchCount += 1;
    });

    return Array.from(map.values())
        .map(group => ({
            ...group,
            rows: [...group.rows].sort(
                (a, b) =>
                    String(a.workCode).localeCompare(String(b.workCode)) ||
                    String(a.workName).localeCompare(String(b.workName), 'ru')
            )
        }))
        .sort((a, b) =>
            String(a.ttLabel).localeCompare(String(b.ttLabel), 'ru')
        );
};

const withTPrefix = value => {
    const raw = normalizeTaskNumber(value);
    if (!raw) return '';
    return /^T/i.test(raw) ? raw : `T${raw}`;
};

const ttLookupKeys = value => {
    const raw = normalizeTaskNumber(value);
    if (!raw) return [];
    const keys = new Set();
    const add = v => {
        const x = normalizeTaskNumber(v);
        if (!x) return;
        keys.add(x);
        keys.add(x.toUpperCase());
        keys.add(withTPrefix(x));
        keys.add(x.replace(/^T+/i, ''));
    };
    add(raw);
    const woMatch = raw.match(/WO(\d+)/i);
    if (woMatch) {
        const wo = `WO${woMatch[1]}`.toUpperCase();
        add(wo);
        add(`T${wo}`);
        add(`T2${wo}`);
        add(woMatch[1]);
        add(woMatch[1].replace(/^0+/, '') || woMatch[1]);
    }
    return [...keys].filter(Boolean);
};

const uniqueTexts = values =>
    [...new Set((values || []).map(v => String(v || '').trim()).filter(Boolean))];

const getExcelValue = (row, ...names) => {
    const keys = Object.keys(row || {});
    for (const name of names) {
        if (row[name] !== undefined && String(row[name]).trim() !== '') {
            return row[name];
        }
        const wanted = String(name).replace(/\s+/g, ' ').trim().toLowerCase();
        const hit = keys.find(
            k => String(k).replace(/\s+/g, ' ').trim().toLowerCase() === wanted
        );
        if (hit && String(row[hit]).trim() !== '') return row[hit];
    }
    return '';
};

const parseAvrReportFile = async file => {
    const arrayBuffer = await file.arrayBuffer();
    const wb = XLSX.read(arrayBuffer, { type: 'array' });
    const sheetName = wb.SheetNames?.[0];
    const sheet = wb.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(sheet, {
        defval: ''
    });

    const byKey = new Map();
    for (const row of rows) {
        const bsNumber = String(getExcelValue(row, 'Номер БС') || '').trim();
        const fromName = splitWorkName(getExcelValue(row, 'Название работ'));
        const workCode = normalizeWorkCode(
            getExcelValue(row, 'Пункт работ') || fromName.code
        );
        const woRaw =
            getExcelValue(row, 'Номер WO НОК', 'WO НОК') ||
            Object.entries(row).find(([k, v]) =>
                /wo/i.test(k) && /нок/i.test(k) && String(v || '').trim()
            )?.[1] ||
            '';
        const claimNumber = normalizeTaskNumber(
            getExcelValue(row, 'Номер заявки')
        );
        const woNumber = normalizeTaskNumber(woRaw);
        const taskNumber = claimNumber || woNumber;
        if (!taskNumber || !workCode || isExcludedWorkCode(workCode)) continue;

        const key = `${taskNumber}__${workCode}`;
        const qty = Number(row['Количество']);
        const amount = Number(row['Стоимость']);
        const comment = [
            getExcelValue(row, 'Описание хода работ'),
            getExcelValue(row, 'Описание задания'),
            getExcelValue(row, 'Причина возврата на доработку')
        ]
            .map(v => String(v || '').trim())
            .filter(Boolean);

        const prev = byKey.get(key);
        byKey.set(key, {
            key,
            taskNumber,
            claimNumber: claimNumber || prev?.claimNumber || '',
            woNumber: woNumber || prev?.woNumber || '',
            bsNumber: bsNumber || prev?.bsNumber || '',
            workCode,
            workName: String(
                fromName.name ||
                    getExcelValue(row, 'Название работ') ||
                    prev?.workName ||
                    ''
            ).trim(),
            quantity:
                roundQty((prev?.quantity || 0) + (Number.isFinite(qty) ? qty : 0)),
            amount:
                roundMoney(
                    (prev?.amount || 0) + (Number.isFinite(amount) ? amount : 0)
                ),
            comments: uniqueTexts([
                ...(prev?.comments || []),
                ...comment
            ]),
            status: row['Статус'] || prev?.status || ''
        });
    }

    return {
        fileName: file.name,
        rawRowCount: rows.length,
        rows: Array.from(byKey.values())
    };
};

const buildComment = row => {
    const parts = [];
    if (row.reportQty === 0 && row.expectedQty > 0) {
        parts.push('Есть в заказе СКС, нет в отчёте Т2');
    } else if (row.expectedQty === 0 && row.reportQty > 0) {
        parts.push('Есть в отчёте Т2, нет в заказе СКС');
    } else if (row.qtyDelta !== 0 && row.amountDelta === 0) {
        parts.push(
            `Инфо: количество разное (заказ СКС ${row.expectedQty}, отчёт Т2 ${row.reportQty}), сумма совпала`
        );
    } else if (row.qtyDelta !== 0) {
        parts.push(
            `Количество: заказ СКС ${row.expectedQty}, отчёт Т2 ${row.reportQty}`
        );
    }

    if (row.amountDelta !== 0) {
        parts.push(
            `Стоимость: заказ СКС ${money(row.expectedAmount)}, отчёт Т2 ${money(row.reportAmount)}`
        );
    }

    if (row.reportComments?.length) {
        parts.push(`Комментарий из отчёта Т2: ${row.reportComments.join('; ')}`);
    }
    if (row.orderComments?.length) {
        parts.push(`Комментарий заказа СКС: ${row.orderComments.join('; ')}`);
    }

    return parts.join('. ');
};

const findExpectedForReport = (reportRow, expectedByAliasWork) => {
    const work = normalizeWorkCode(reportRow.workCode);
    const lookup = keys => {
        for (const n of keys) {
            const hit = expectedByAliasWork.get(
                `${String(n).toUpperCase()}__${work}`
            );
            if (hit) return hit;
        }
        return null;
    };
    return (
        lookup(ttLookupKeys(reportRow.claimNumber)) ||
        lookup(ttLookupKeys(reportRow.woNumber))
    );
};

const mergeCompareRow = (prev, next) => {
    if (!prev) return next;
    const reportQty = roundQty((prev.reportQty || 0) + (next.reportQty || 0));
    const expectedQty = roundQty(
        Math.max(prev.expectedQty || 0, next.expectedQty || 0)
    );
    const reportAmount = roundMoney(
        (prev.reportAmount || 0) + (next.reportAmount || 0)
    );
    const expectedAmount = roundMoney(
        Math.max(prev.expectedAmount || 0, next.expectedAmount || 0)
    );
    return {
        ...prev,
        ...next,
        inOrder: prev.inOrder || next.inOrder,
        inReport: prev.inReport || next.inReport,
        reportQty,
        expectedQty,
        reportAmount,
        expectedAmount,
        qtyDelta: roundQty(expectedQty - reportQty),
        amountDelta: roundMoney(expectedAmount - reportAmount),
        reportComments: uniqueTexts([
            ...(prev.reportComments || []),
            ...(next.reportComments || [])
        ]),
        orderComments: uniqueTexts([
            ...(prev.orderComments || []),
            ...(next.orderComments || [])
        ]),
        claimNumber: prev.claimNumber || next.claimNumber,
        woNumber: prev.woNumber || next.woNumber,
        taskNumber: prev.taskNumber || next.taskNumber,
        bsNumber: prev.bsNumber || next.bsNumber,
        workName: prev.workName || next.workName,
        invoiceIds: [
            ...new Set([
                ...(prev.invoiceIds || []),
                ...(next.invoiceIds || [])
            ])
        ],
        invoiceLines: [
            ...(prev.invoiceLines || []),
            ...(next.invoiceLines || [])
        ]
    };
};

const CustomerOrderComparePage = () => {
    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [orderId, setOrderId] = useState(null);

    const [reportLoading, setReportLoading] = useState(false);
    const [report, setReport] = useState(null);

    const [expectedLoading, setExpectedLoading] = useState(false);
    const [expected, setExpected] = useState(null);

    const [viewMode, setViewMode] = useState('discrepant_full');
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [agreeOpen, setAgreeOpen] = useState(false);
    const [agreeApplying, setAgreeApplying] = useState(false);
    const [applyFeedback, setApplyFeedback] = useState(null);
    /** Явный набор строк для модалки (массовое согласование); иначе — selectedRows */
    const [agreeTargetRows, setAgreeTargetRows] = useState(null);
    /** Комментарии для заказчика по ключу строки сверки */
    const [customerComments, setCustomerComments] = useState({});

    const openAgreeModal = useCallback(() => {
        setApplyFeedback(null);
        setAgreeTargetRows(null);
        setAgreeOpen(true);
    }, []);

    const showApplyErrorDetails = useCallback((title, errors, fallbackMessage) => {
        const lines = (errors || [])
            .map(item => {
                if (typeof item === 'string') return item;
                const prefix = item?.key ? `${item.key}: ` : '';
                return `${prefix}${item?.message || String(item)}`;
            })
            .filter(Boolean);

        if (!lines.length && fallbackMessage) {
            lines.push(fallbackMessage);
        }

        setApplyFeedback({
            type: 'error',
            lines
        });

        Modal.error({
            title,
            width: 680,
            content: (
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {lines.map((line, index) => (
                        <li key={`${index}-${line.slice(0, 24)}`}>{line}</li>
                    ))}
                </ul>
            )
        });
    }, []);

    const [taskModalOpen, setTaskModalOpen] = useState(false);
    const [taskModalLoading, setTaskModalLoading] = useState(false);
    const [taskModal, setTaskModal] = useState(null);
    const [rowSearch, setRowSearch] = useState('');
    const [taskPhotos, setTaskPhotos] = useState([]);
    const [taskPhotoUrls, setTaskPhotoUrls] = useState({});
    const [taskOriginalUrls, setTaskOriginalUrls] = useState({});

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`
    });

    const loadOrders = useCallback(async () => {
        setOrdersLoading(true);
        try {
            const { data } = await axios.get(
                '/api/registries/available-orders',
                {
                    params: { type: 'customer_act' },
                    headers: authHeaders()
                }
            );
            const openOrders = (data || []).filter(
                o => String(o.status || '').toLowerCase() !== 'closed'
            );
            setOrders(openOrders);
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    error.response?.data?.message ||
                    error.message ||
                    'Не удалось загрузить заказы покупателя'
            );
        } finally {
            setOrdersLoading(false);
        }
    }, []);

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    const allRows = useMemo(() => {
        const expectedRows = (expected?.rows || []).filter(
            row => !isExcludedWorkCode(row.workCode)
        );
        const reportRows = (report?.rows || []).filter(
            row => !isExcludedWorkCode(row.workCode)
        );
        if (!expected) return [];

        const expectedByAliasWork = new Map();
        expectedRows.forEach(row => {
            const aliases = row.aliases?.length
                ? row.aliases
                : ttLookupKeys(row.nomer_tt);
            aliases.forEach(alias => {
                const key = `${normalizeTaskNumber(alias).toUpperCase()}__${normalizeWorkCode(row.workCode)}`;
                if (alias && row.workCode) expectedByAliasWork.set(key, row);
            });
        });

        const used = new Set();
        const merged = new Map();

        const pushRow = (key, row) => {
            const next = mergeCompareRow(merged.get(key), row);
            let kind = 'matched';
            if (next.inOrder && !next.inReport) kind = 'missing_in_report';
            else if (!next.inOrder && next.inReport) kind = 'extra_in_report';
            else if (next.amountDelta !== 0) {
                /* Проверка позиции по сумме */
                kind = 'mismatch';
            } else if (next.qtyDelta !== 0) {
                /* Кол-во разное, сумма одна — только информация */
                kind = 'qty_info';
            }
            next.kind = kind;
            next.comment =
                kind === 'matched' ? '' : buildComment(next);
            merged.set(key, next);
        };

        reportRows.forEach(reportRow => {
            const expectedRow = findExpectedForReport(
                reportRow,
                expectedByAliasWork
            );
            const workCode = normalizeWorkCode(
                expectedRow?.workCode || reportRow.workCode
            );
            const key = expectedRow
                ? `${expectedRow.task_id}__${workCode}`
                : `report__${reportRow.key}`;
            if (expectedRow) used.add(key);

            const reportQty = roundQty(reportRow.quantity);
            const expectedQty = roundQty(expectedRow?.quantity);
            const reportAmount = roundMoney(reportRow.amount);
            const expectedAmount = roundMoney(expectedRow?.amount);

            pushRow(key, {
                key,
                inOrder: Boolean(expectedRow),
                inReport: true,
                taskId: expectedRow?.task_id || null,
                taskNumber:
                    reportRow.claimNumber ||
                    expectedRow?.nomer_tt ||
                    reportRow.taskNumber ||
                    reportRow.woNumber ||
                    '',
                claimNumber:
                    reportRow.claimNumber ||
                    String(expectedRow?.nomer_tt || ''),
                woNumber:
                    reportRow.woNumber ||
                    withTPrefix(expectedRow?.nomer_tt),
                bsNumber:
                    expectedRow?.bsNumber || reportRow.bsNumber || '',
                workCode,
                workName:
                    expectedRow?.workName || reportRow.workName || '',
                expectedQty,
                reportQty,
                qtyDelta: roundQty(expectedQty - reportQty),
                expectedAmount,
                reportAmount,
                amountDelta: roundMoney(expectedAmount - reportAmount),
                reportComments: reportRow.comments || [],
                orderComments: expectedRow?.comments || [],
                invoiceIds: expectedRow?.invoice_ids || [],
                invoiceLines: expectedRow?.invoices || []
            });
        });

        expectedRows.forEach(expectedRow => {
            const workCode = normalizeWorkCode(expectedRow.workCode);
            const key = `${expectedRow.task_id}__${workCode}`;
            if (used.has(key) || merged.has(key)) return;

            const expectedQty = roundQty(expectedRow.quantity);
            const expectedAmount = roundMoney(expectedRow.amount);
            pushRow(key, {
                key,
                inOrder: true,
                inReport: false,
                taskId: expectedRow.task_id,
                taskNumber: expectedRow.nomer_tt || '',
                claimNumber: String(expectedRow.nomer_tt || ''),
                woNumber: withTPrefix(expectedRow.nomer_tt),
                bsNumber: expectedRow.bsNumber || '',
                workCode,
                workName: expectedRow.workName || '',
                expectedQty,
                reportQty: 0,
                qtyDelta: expectedQty,
                expectedAmount,
                reportAmount: 0,
                amountDelta: expectedAmount,
                reportComments: [],
                orderComments: expectedRow.comments || [],
                invoiceIds: expectedRow.invoice_ids || [],
                invoiceLines: expectedRow.invoices || []
            });
        });

        return Array.from(merged.values()).sort(
            (a, b) =>
                String(a.taskNumber).localeCompare(String(b.taskNumber), 'ru') ||
                String(a.bsNumber).localeCompare(String(b.bsNumber), 'ru') ||
                String(a.workCode).localeCompare(String(b.workCode))
        );
    }, [report, expected]);

    const discrepancyRows = useMemo(
        () => allRows.filter(r => !isSumOkKind(r.kind)),
        [allRows]
    );

    const smallSideRows = useMemo(
        () => allRows.filter(isSmallSideDiscrepancy),
        [allRows]
    );

    const counts = useMemo(
        () => ({
            all: allRows.length,
            matched: allRows.filter(r => r.kind === 'matched').length,
            qty_info: allRows.filter(r => r.kind === 'qty_info').length,
            mismatch: allRows.filter(r => r.kind === 'mismatch').length,
            missing_in_report: allRows.filter(
                r => r.kind === 'missing_in_report'
            ).length,
            extra_in_report: allRows.filter(
                r => r.kind === 'extra_in_report'
            ).length,
            small_side: smallSideRows.length,
            discrepancies: discrepancyRows.length
        }),
        [allRows, discrepancyRows, smallSideRows]
    );

    useEffect(() => {
        setSelectedRowKeys([]);
        setAgreeOpen(false);
        setAgreeTargetRows(null);
        setCustomerComments({});
    }, [report, expected]);

    const discrepantTaskKeys = useMemo(() => {
        const keys = new Set();
        allRows.forEach(row => {
            if (row.kind && !isSumOkKind(row.kind)) {
                keys.add(taskGroupKey(row));
            }
        });
        return keys;
    }, [allRows]);

    const comparisonRows = useMemo(() => {
        const byMode =
            viewMode === 'all'
                ? allRows
                : viewMode === 'sides'
                  ? allRows.filter(
                        r =>
                            r.kind === 'missing_in_report' ||
                            r.kind === 'extra_in_report'
                    )
                  : viewMode === 'small_side'
                    ? smallSideRows
                    : viewMode === 'discrepant_full'
                      ? allRows.filter(r =>
                            discrepantTaskKeys.has(taskGroupKey(r))
                        )
                      : allRows.filter(r => r.kind === viewMode);
        const q = String(rowSearch || '')
            .trim()
            .toLowerCase();
        if (!q) return byMode;
        return byMode.filter(row => {
            const hay = [
                row.taskNumber,
                row.claimNumber,
                row.woNumber,
                row.bsNumber,
                row.taskId,
                row.workCode,
                row.workName,
                customerComments[row.key]
            ]
                .map(v => String(v || '').toLowerCase())
                .join(' ');
            return hay.includes(q);
        });
    }, [
        allRows,
        viewMode,
        rowSearch,
        smallSideRows,
        discrepantTaskKeys,
        customerComments
    ]);

    const comparisonTaskGroups = useMemo(
        () => buildTaskGroups(comparisonRows),
        [comparisonRows]
    );

    const openMassAgreeSmallModal = useCallback(() => {
        if (!smallSideRows.length) {
            message.info(
                `Нет мелких расхождений до ${SMALL_DISCREPANCY_LIMIT} ₽`
            );
            return;
        }
        setApplyFeedback(null);
        setAgreeTargetRows(smallSideRows);
        setSelectedRowKeys(smallSideRows.map(r => r.key));
        setAgreeOpen(true);
    }, [smallSideRows]);

    const isSideBySideMode =
        viewMode === 'missing_in_report' ||
        viewMode === 'extra_in_report' ||
        viewMode === 'sides';

    const isDiscrepantFullMode = viewMode === 'discrepant_full';

    const openCustomerOrder = useCallback(() => {
        if (!orderId) {
            message.warning('Сначала выберите заказ покупателя');
            return;
        }
        window.open(`/docs?id=${orderId}`, '_blank', 'noopener,noreferrer');
    }, [orderId]);

    /** Группы по ТТ: слева «нет в отчёте Т2», справа «нет в заказе СКС» */
    const ttSideGroups = useMemo(() => {
        if (!isSideBySideMode) return [];

        const q = String(rowSearch || '')
            .trim()
            .toLowerCase();
        const sideRows = allRows.filter(
            r =>
                r.kind === 'missing_in_report' ||
                r.kind === 'extra_in_report'
        );
        const filtered = !q
            ? sideRows
            : sideRows.filter(row => {
                  const hay = [
                      row.taskNumber,
                      row.claimNumber,
                      row.woNumber,
                      row.bsNumber,
                      row.taskId,
                      row.workCode,
                      row.workName
                  ]
                      .map(v => String(v || '').toLowerCase())
                      .join(' ');
                  return hay.includes(q);
              });

        const map = new Map();
        filtered.forEach(row => {
            const ttRaw =
                row.claimNumber ||
                row.taskNumber ||
                row.woNumber ||
                (row.taskId ? `ID ${row.taskId}` : '—');
            const groupKey =
                normalizeTaskNumber(ttRaw).toUpperCase() ||
                `TASK:${row.taskId || row.key}`;
            if (!map.has(groupKey)) {
                map.set(groupKey, {
                    key: groupKey,
                    ttLabel: ttRaw,
                    bsNumber: row.bsNumber || '',
                    taskId: row.taskId || null,
                    left: [],
                    right: [],
                    sumExpected: 0,
                    sumReport: 0
                });
            }
            const group = map.get(groupKey);
            if (row.bsNumber && !group.bsNumber) {
                group.bsNumber = row.bsNumber;
            }
            if (row.taskId && !group.taskId) {
                group.taskId = row.taskId;
            }
            if (row.kind === 'missing_in_report') {
                group.left.push(row);
                group.sumExpected = roundMoney(
                    group.sumExpected + (Number(row.expectedAmount) || 0)
                );
            } else {
                group.right.push(row);
                group.sumReport = roundMoney(
                    group.sumReport + (Number(row.reportAmount) || 0)
                );
            }
        });

        let groups = Array.from(map.values()).sort((a, b) =>
            String(a.ttLabel).localeCompare(String(b.ttLabel), 'ru')
        );

        if (viewMode === 'missing_in_report') {
            groups = groups.filter(g => g.left.length);
        } else if (viewMode === 'extra_in_report') {
            groups = groups.filter(g => g.right.length);
        }

        return groups;
    }, [allRows, isSideBySideMode, rowSearch, viewMode]);

    const sideColumns = useMemo(
        () => [
            {
                title: 'Пункт',
                dataIndex: 'workCode',
                width: 110
            },
            {
                title: 'Наименование',
                dataIndex: 'workName',
                ellipsis: true,
                width: 280,
                render: v => v || '—'
            },
            {
                title: 'Кол-во',
                key: 'qty',
                width: 100,
                align: 'right',
                render: (_, row) =>
                    row.kind === 'missing_in_report'
                        ? row.expectedQty
                        : row.reportQty
            },
            {
                title: 'Сумма',
                key: 'amount',
                width: 130,
                align: 'right',
                render: (_, row) =>
                    money(
                        row.kind === 'missing_in_report'
                            ? row.expectedAmount
                            : row.reportAmount
                    )
            }
        ],
        []
    );

    // legacy duplicate block removed below — keep selectedRows next

    const setCustomerComment = useCallback((rowKey, value) => {
        setCustomerComments(prev => ({
            ...prev,
            [rowKey]: value
        }));
    }, []);

    const selectedRows = useMemo(
        () => allRows.filter(r => selectedRowKeys.includes(r.key)),
        [allRows, selectedRowKeys]
    );

    const rowsForAgree = useMemo(() => {
        const source = agreeTargetRows?.length
            ? agreeTargetRows
            : selectedRows;
        return source.filter(r => r.kind && r.kind !== 'matched');
    }, [agreeTargetRows, selectedRows]);

    const revokeBlobUrls = useCallback(urls => {
        Object.values(urls || {}).forEach(url => {
            if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
        });
    }, []);

    const closeTaskModal = useCallback(() => {
        revokeBlobUrls(taskPhotoUrls);
        revokeBlobUrls(taskOriginalUrls);
        setTaskPhotoUrls({});
        setTaskOriginalUrls({});
        setTaskPhotos([]);
        setTaskModal(null);
        setTaskModalOpen(false);
    }, [revokeBlobUrls, taskPhotoUrls, taskOriginalUrls]);

    useEffect(
        () => () => {
            revokeBlobUrls(taskPhotoUrls);
            revokeBlobUrls(taskOriginalUrls);
        },
        [revokeBlobUrls, taskPhotoUrls, taskOriginalUrls]
    );

    const loadProtectedImage = useCallback(async url => {
        const response = await axios.get(url, {
            headers: authHeaders(),
            responseType: 'blob'
        });
        return URL.createObjectURL(response.data);
    }, []);

    const openTaskModal = useCallback(
        async row => {
            if (!row?.taskId && !row?.claimNumber && !row?.woNumber && !row?.taskNumber) {
                message.warning('Не указан номер заявки');
                return;
            }

            revokeBlobUrls(taskPhotoUrls);
            revokeBlobUrls(taskOriginalUrls);
            setTaskPhotoUrls({});
            setTaskOriginalUrls({});
            setTaskPhotos([]);
            setTaskModal(null);
            setTaskModalOpen(true);
            setTaskModalLoading(true);

            try {
                const { data } = await axios.get(
                    '/api/customer-order-compare/task-preview',
                    {
                        params: {
                            taskId: row.taskId || undefined,
                            number: row.claimNumber || row.taskNumber || undefined,
                            woNumber: row.woNumber || undefined
                        },
                        headers: authHeaders()
                    }
                );
                const preview = data.data || data || {};
                const task = preview.task || {};
                const income = Array.isArray(preview.income)
                    ? preview.income
                    : [];

                let gepard = null;
                let photos = [];
                const externalId = task.external_id;
                if (externalId) {
                    const extRes = await axios.get(
                        `/api/tasks/ext/${encodeURIComponent(externalId)}`,
                        { headers: authHeaders() }
                    );
                    gepard = extRes.data || null;
                    const attachments = Array.isArray(gepard?.attachments)
                        ? gepard.attachments
                        : [];
                    photos = attachments
                        .filter(attachment => {
                            const extension = String(
                                attachment?.extension || ''
                            ).toLowerCase();
                            const isPhoto =
                                attachment?.type === 'PHOTO' ||
                                attachment?.semantics === 'PHOTO' ||
                                ['jpg', 'jpeg', 'png', 'webp'].includes(
                                    extension
                                );
                            const isDeleted =
                                attachment?.deleted === 't' ||
                                attachment?.deleted === true;
                            return isPhoto && !isDeleted && attachment?.num;
                        })
                        .map(attachment => ({
                            ...attachment,
                            preview_url: `/api/tasks/ext/${encodeURIComponent(externalId)}/photos/${encodeURIComponent(attachment.num)}/crop/w400/h300`,
                            original_url: `/api/tasks/ext/${encodeURIComponent(externalId)}/photos/${encodeURIComponent(attachment.num)}`
                        }));
                }

                const gepardCf =
                    typeof gepard?.custom_fields === 'string'
                        ? (() => {
                              try {
                                  return JSON.parse(gepard.custom_fields);
                              } catch (_) {
                                  return {};
                              }
                          })()
                        : gepard?.custom_fields || {};
                const cfVal = key => {
                    const field = gepardCf?.[key];
                    if (field == null) return '';
                    if (typeof field === 'object') {
                        return String(field.value ?? field.val ?? '');
                    }
                    return String(field);
                };

                setTaskPhotos(photos);
                setTaskModal({
                    task: {
                        ...task,
                        work_done_description:
                            task.work_done_description ||
                            cfVal('Opisanie_vepolnenneh_rabot') ||
                            '',
                        executor_comments:
                            task.executor_comments ||
                            cfVal('comments') ||
                            ''
                    },
                    income,
                    gepard,
                    lookup: {
                        number:
                            row.claimNumber ||
                            row.taskNumber ||
                            '',
                        woNumber: row.woNumber || '',
                        taskId: row.taskId || null,
                        bsNumber: row.bsNumber || ''
                    }
                });

                const [previewResults, originalResults] = await Promise.all([
                    Promise.allSettled(
                        photos.map(async photo => ({
                            num: photo.num,
                            url: await loadProtectedImage(photo.preview_url)
                        }))
                    ),
                    Promise.allSettled(
                        photos.map(async photo => ({
                            num: photo.num,
                            url: await loadProtectedImage(photo.original_url)
                        }))
                    )
                ]);

                const previews = {};
                previewResults.forEach(result => {
                    if (result.status === 'fulfilled') {
                        previews[result.value.num] = result.value.url;
                    }
                });
                const originals = {};
                originalResults.forEach(result => {
                    if (result.status === 'fulfilled') {
                        originals[result.value.num] = result.value.url;
                    }
                });
                setTaskPhotoUrls(previews);
                setTaskOriginalUrls(originals);
            } catch (error) {
                message.error(
                    error.response?.data?.message ||
                        error.message ||
                        'Не удалось загрузить заявку'
                );
            } finally {
                setTaskModalLoading(false);
            }
        },
        [loadProtectedImage, revokeBlobUrls, taskPhotoUrls, taskOriginalUrls]
    );

    const columns = useMemo(
        () => [
            {
                title: 'Статус',
                dataIndex: 'kind',
                width: 150,
                render: v => kindTag(v)
            },
            {
                title: 'Заявка',
                dataIndex: 'taskNumber',
                width: 110,
                render: (v, row) =>
                    v ? (
                        <Button
                            type="link"
                            style={{ padding: 0, height: 'auto' }}
                            onClick={() => openTaskModal(row)}
                        >
                            {v}
                        </Button>
                    ) : (
                        '—'
                    )
            },
            {
                title: 'БС',
                dataIndex: 'bsNumber',
                width: 80,
                ellipsis: true
            },
            {
                title: 'Пункт',
                dataIndex: 'workCode',
                width: 72,
                ellipsis: true
            },
            {
                title: 'Наименование',
                dataIndex: 'workName',
                width: 180,
                ellipsis: true,
                render: v => v || '—'
            },
            {
                title: 'Кол СКС',
                dataIndex: 'expectedQty',
                align: 'right',
                width: 72
            },
            {
                title: 'Кол Т2',
                dataIndex: 'reportQty',
                align: 'right',
                width: 72
            },
            {
                title: 'Δ кол',
                dataIndex: 'qtyDelta',
                align: 'right',
                width: 70,
                render: (v, row) =>
                    v === 0 ? (
                        <Tag>0</Tag>
                    ) : row.kind === 'qty_info' ? (
                        <Tag color="blue">{v > 0 ? `+${v}` : v}</Tag>
                    ) : v > 0 ? (
                        <Tag color="orange">+{v}</Tag>
                    ) : (
                        <Tag color="red">{v}</Tag>
                    )
            },
            {
                title: 'Σ СКС',
                dataIndex: 'expectedAmount',
                align: 'right',
                width: 96,
                render: v => money(v)
            },
            {
                title: 'Σ Т2',
                dataIndex: 'reportAmount',
                align: 'right',
                width: 96,
                render: v => money(v)
            },
            {
                title: 'Δ Σ',
                dataIndex: 'amountDelta',
                align: 'right',
                width: 96,
                render: v =>
                    v === 0 ? (
                        <Tag>0</Tag>
                    ) : v > 0 ? (
                        <Tag color="orange">+{money(v)}</Tag>
                    ) : (
                        <Tag color="red">{money(v)}</Tag>
                    )
            },
            {
                title: 'Автокомментарий',
                dataIndex: 'comment',
                width: 220,
                ellipsis: true,
                onCell: () => ({
                    style: {
                        maxWidth: 260,
                        whiteSpace: 'normal',
                        fontSize: 12,
                        lineHeight: 1.35
                    }
                }),
                render: v =>
                    v ? (
                        <Text
                            style={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                display: 'block',
                                lineHeight: 1.35,
                                fontSize: 12
                            }}
                        >
                            {v}
                        </Text>
                    ) : (
                        '—'
                    )
            },
            {
                title: 'Коммент. заказчику',
                key: 'customerComment',
                width: 180,
                fixed: 'right',
                onCell: () => ({
                    style: {
                        minWidth: 160,
                        maxWidth: 220,
                        whiteSpace: 'normal'
                    }
                }),
                render: (_, row) => (
                    <TextArea
                        autoSize={{ minRows: 1, maxRows: 3 }}
                        placeholder="С чем не согласны…"
                        value={customerComments[row.key] || ''}
                        onClick={e => e.stopPropagation()}
                        onChange={e =>
                            setCustomerComment(row.key, e.target.value)
                        }
                        disabled={isSumOkKind(row.kind)}
                    />
                )
            }
        ],
        [openTaskModal, customerComments, setCustomerComment]
    );

    const fetchExpected = useCallback(async () => {
        if (!orderId) {
            message.warning('Выберите заказ покупателя');
            return;
        }
        if (!report?.rows?.length) {
            message.warning('Сначала загрузите Excel отчёт');
            return;
        }

        setExpectedLoading(true);
        setExpected(null);
        try {
            const { data } = await axios.get(
                '/api/customer-order-compare/expected',
                {
                    params: { orderId },
                    headers: authHeaders()
                }
            );
            setExpected(data.data || data || null);
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Не удалось получить ожидаемые значения'
            );
        } finally {
            setExpectedLoading(false);
        }
    }, [orderId, report]);

    const applyAgreement = useCallback(async () => {
        if (!orderId) {
            message.warning('Выберите заказ покупателя');
            return;
        }

        const actionable = rowsForAgree;
        if (!actionable.length) {
            message.info('Среди выбранных нет строк для изменения ВДР');
            return;
        }

        setApplyFeedback(null);
        setAgreeApplying(true);
        try {
            const { data } = await axios.post(
                '/api/customer-order-compare/apply',
                {
                    orderId,
                    rows: actionable.map(row => ({
                        key: row.key,
                        kind: row.kind,
                        taskId: row.taskId,
                        taskNumber: row.taskNumber,
                        claimNumber: row.claimNumber,
                        woNumber: row.woNumber,
                        workCode: row.workCode,
                        workName: row.workName,
                        reportQty: row.reportQty,
                        reportAmount: row.reportAmount,
                        invoiceIds: row.invoiceIds || [],
                        invoiceLines: row.invoiceLines || []
                    }))
                },
                { headers: authHeaders() }
            );

            if (data?.success === false) {
                throw new Error(data.message || 'Не удалось применить корректировки');
            }

            const result = data?.data || data || {};
            if (result.errors?.length) {
                showApplyErrorDetails(
                    'Часть строк не применена',
                    result.errors,
                    result.message
                );
                message.warning(
                    result.message ||
                        `Применено частично: ошибок ${result.errors.length}`
                );
            } else {
                message.success(
                    result.message || 'Корректировки ВДР применены'
                );
                setAgreeOpen(false);
                setAgreeTargetRows(null);
                setSelectedRowKeys([]);
            }
            await fetchExpected();
        } catch (error) {
            const responseData = error.response?.data || {};
            const details = responseData.details || {};
            const rowErrors = Array.isArray(details.errors)
                ? details.errors
                : Array.isArray(responseData.errors)
                  ? responseData.errors
                  : [];
            const errorMessage =
                responseData.message ||
                error.message ||
                'Не удалось применить корректировки';

            if (rowErrors.length) {
                showApplyErrorDetails(
                    'Ошибки применения',
                    rowErrors,
                    errorMessage
                );
            } else {
                setApplyFeedback({
                    type: 'error',
                    lines: [errorMessage]
                });
            }
            message.error(errorMessage);
        } finally {
            setAgreeApplying(false);
        }
    }, [
        orderId,
        rowsForAgree,
        fetchExpected,
        showApplyErrorDetails
    ]);

    const downloadDiscrepancies = useCallback(() => {
        const rows = selectedRowKeys.length
            ? allRows.filter(r => selectedRowKeys.includes(r.key))
            : viewMode === 'discrepant_full'
              ? allRows.filter(r => discrepantTaskKeys.has(taskGroupKey(r)))
              : discrepancyRows;

        if (!rows.length) {
            message.info('Нет строк для выгрузки');
            return;
        }

        const sorted = [...rows].sort(
            (a, b) =>
                String(a.taskNumber).localeCompare(String(b.taskNumber), 'ru') ||
                String(a.workCode).localeCompare(String(b.workCode))
        );

        const sheetRows = [];
        let prevTask = null;
        let taskSumSk = 0;
        let taskSumT2 = 0;
        let taskSumDelta = 0;

        const flushTaskTotal = () => {
            if (prevTask == null) return;
            sheetRows.push({
                Статус: 'ИТОГО ПО ЗАЯВКЕ',
                'Номер заявки': prevTask,
                'Номер БС': '',
                'Пункт работ': '',
                Название: '',
                'Кол-во СКС': '',
                'Кол-во Т2': '',
                'Δ кол-во': '',
                'Сумма СКС': taskSumSk,
                'Сумма Т2': taskSumT2,
                'Δ сумма': taskSumDelta,
                Автокомментарий: '',
                'Комментарий для заказчика': ''
            });
            taskSumSk = 0;
            taskSumT2 = 0;
            taskSumDelta = 0;
        };

        sorted.forEach(r => {
            const task = r.taskNumber || '';
            if (prevTask !== null && task !== prevTask) {
                flushTaskTotal();
            }
            prevTask = task;
            taskSumSk = roundMoney(taskSumSk + (Number(r.expectedAmount) || 0));
            taskSumT2 = roundMoney(taskSumT2 + (Number(r.reportAmount) || 0));
            taskSumDelta = roundMoney(
                taskSumDelta + (Number(r.amountDelta) || 0)
            );
            sheetRows.push({
                Статус: kindPlain(r.kind),
                'Номер заявки': r.taskNumber,
                'Номер БС': r.bsNumber,
                'Пункт работ': r.workCode,
                Название: r.workName,
                'Кол-во СКС': r.expectedQty,
                'Кол-во Т2': r.reportQty,
                'Δ кол-во': r.qtyDelta,
                'Сумма СКС': r.expectedAmount,
                'Сумма Т2': r.reportAmount,
                'Δ сумма': r.amountDelta,
                Автокомментарий: r.comment,
                'Комментарий для заказчика':
                    customerComments[r.key] || ''
            });
        });
        flushTaskTotal();

        const ws = XLSX.utils.json_to_sheet(sheetRows);
        ws['!cols'] = [
            { wch: 42 },
            { wch: 16 },
            { wch: 12 },
            { wch: 12 },
            { wch: 40 },
            { wch: 12 },
            { wch: 12 },
            { wch: 10 },
            { wch: 14 },
            { wch: 14 },
            { wch: 12 },
            { wch: 40 },
            { wch: 40 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Сверка');
        XLSX.writeFile(
            wb,
            `customer-order-compare_${orderId || 'order'}_${Date.now()}.xlsx`
        );
        message.success(
            selectedRowKeys.length
                ? `В файл: выбранные (${rows.length})`
                : `В файл: ${rows.length} строк`
        );
    }, [
        allRows,
        discrepancyRows,
        orderId,
        selectedRowKeys,
        viewMode,
        discrepantTaskKeys,
        customerComments
    ]);

    const modalTask = taskModal?.task || {};
    const gepardTask = taskModal?.gepard || {};
    const incomeInvoices = Array.isArray(taskModal?.income)
        ? taskModal.income
        : [];
    const incomeTotal = incomeInvoices.reduce(
        (sum, row) => sum + (Number(row.amount) || 0),
        0
    );

    const taskCompareRows = useMemo(() => {
        if (!taskModalOpen) return [];
        const keys = new Set([
            ...ttLookupKeys(modalTask.external_id),
            ...ttLookupKeys(modalTask.nomer_tt),
            ...ttLookupKeys(taskModal?.lookup?.number),
            ...ttLookupKeys(taskModal?.lookup?.woNumber)
        ].map(v => normalizeTaskNumber(v).toUpperCase()).filter(Boolean));

        const taskId = Number(modalTask.id);
        return allRows.filter(row => {
            if (taskId && Number(row.taskId) === taskId) return true;
            const rowKeys = [
                ...ttLookupKeys(row.claimNumber),
                ...ttLookupKeys(row.taskNumber),
                ...ttLookupKeys(row.woNumber)
            ].map(v => normalizeTaskNumber(v).toUpperCase());
            return rowKeys.some(k => keys.has(k));
        });
    }, [taskModalOpen, taskModal, modalTask, allRows]);

    const taskCompareTotals = useMemo(() => {
        return taskCompareRows.reduce(
            (acc, row) => ({
                expected: roundMoney(
                    acc.expected + (Number(row.expectedAmount) || 0)
                ),
                report: roundMoney(
                    acc.report + (Number(row.reportAmount) || 0)
                ),
                delta: roundMoney(
                    acc.delta + (Number(row.amountDelta) || 0)
                )
            }),
            { expected: 0, report: 0, delta: 0 }
        );
    }, [taskCompareRows]);

    const taskCompareColumns = useMemo(
        () => [
            {
                title: 'Статус',
                dataIndex: 'kind',
                width: 200,
                render: v => kindTag(v)
            },
            {
                title: 'Пункт',
                dataIndex: 'workCode',
                width: 110
            },
            {
                title: 'Наименование',
                dataIndex: 'workName',
                width: 280,
                render: v => v || '—'
            },
            {
                title: 'Кол-во СКС',
                dataIndex: 'expectedQty',
                width: 100,
                align: 'right'
            },
            {
                title: 'Кол-во Т2',
                dataIndex: 'reportQty',
                width: 100,
                align: 'right'
            },
            {
                title: 'Сумма СКС',
                dataIndex: 'expectedAmount',
                width: 120,
                align: 'right',
                render: v => money(v)
            },
            {
                title: 'Сумма Т2',
                dataIndex: 'reportAmount',
                width: 120,
                align: 'right',
                render: v => money(v)
            },
            {
                title: 'Δ',
                dataIndex: 'amountDelta',
                width: 110,
                align: 'right',
                render: v => money(v)
            },
            {
                title: 'Комментарий для заказчика',
                key: 'customerComment',
                width: 260,
                render: (_, row) => (
                    <TextArea
                        autoSize={{
                            minRows: 1,
                            maxRows: 4
                        }}
                        placeholder="С чем не согласны…"
                        value={
                            customerComments[
                                row.key
                            ] || ''
                        }
                        onChange={e =>
                            setCustomerComment(
                                row.key,
                                e.target.value
                            )
                        }
                    />
                )
            }
        ],
        [customerComments, setCustomerComment]
    );

    const incomeColumns = [
        {
            title: 'Наименование',
            dataIndex: 'rate_name',
            width: 320
        },
        {
            title: 'Кол-во',
            dataIndex: 'quantity',
            width: 100,
            align: 'right'
        },
        {
            title: 'Ед.',
            dataIndex: 'unit',
            width: 80
        },
        {
            title: 'Цена',
            dataIndex: 'price',
            width: 130,
            align: 'right',
            render: v => money(v)
        },
        {
            title: 'Сумма',
            dataIndex: 'amount',
            width: 140,
            align: 'right',
            render: v => <Text strong>{money(v)}</Text>
        },
        {
            title: 'Примечание',
            dataIndex: 'note',
            width: 220,
            render: v => v || '—'
        }
    ];

    const [columnWidths, setColumnWidths] = useUserUiSetting(
        'customer-order-compare:column-widths',
        {}
    );
    const mainTable = useResizableColumns(
        columns,
        'main',
        columnWidths,
        setColumnWidths,
        32
    );
    const sideTable = useResizableColumns(
        sideColumns,
        'side',
        columnWidths,
        setColumnWidths,
        32
    );
    const taskCompareTable = useResizableColumns(
        taskCompareColumns,
        'task',
        columnWidths,
        setColumnWidths
    );
    const incomeTable = useResizableColumns(
        incomeColumns,
        'income',
        columnWidths,
        setColumnWidths
    );

    return (
        <Layout>
            <div style={{ padding: 20, maxWidth: 2100, margin: '0 auto' }}>
                <Title level={3}>
                    Проверка заказа покупателя
                </Title>
                <Text type="secondary">
                    Сверка заказа СКС с отчётом Т2 по номеру ТТ. Сначала колонка
                    «Номер заявки» как есть, без добавления T. Если не нашли —
                    «Номер WO НОК» тоже по ТТ, с вариантами T / T2WO.
                </Text>

                <div style={{ marginTop: 16 }}>
                    <Card title="1) Загрузите Excel отчёт">
                        <Upload
                            accept=".xlsx,.xls"
                            showUploadList={false}
                            beforeUpload={async file => {
                                setReportLoading(true);
                                setExpected(null);
                                setReport(null);
                                try {
                                    const parsed =
                                        await parseAvrReportFile(file);
                                    setReport(parsed);
                                    message.success(
                                        'Отчёт загружен и распарсен'
                                    );
                                } catch (e) {
                                    message.error(
                                        e.message ||
                                            'Ошибка чтения Excel'
                                    );
                                } finally {
                                    setReportLoading(false);
                                }
                                return false;
                            }}
                        >
                            <Button
                                icon={<UploadOutlined />}
                                loading={reportLoading}
                            >
                                Выбрать файл
                            </Button>
                        </Upload>

                        {report ? (
                            <div style={{ marginTop: 12 }}>
                                <Tag color="blue">{report.fileName}</Tag>
                                <div style={{ marginTop: 8 }}>
                                    <Text type="secondary">
                                        Строк в файле: {report.rawRowCount}
                                        {' · '}
                                        позиций: {report.rows.length}
                                    </Text>
                                </div>
                            </div>
                        ) : null}
                    </Card>
                </div>

                <div style={{ marginTop: 16 }}>
                    <Card title="2) Выберите незакрытый заказ покупателя">
                        <Space wrap>
                            <Select
                                showSearch
                                optionFilterProp="label"
                                style={{ minWidth: 420 }}
                                placeholder="Выберите заказ покупателя"
                                options={orders.map(o => ({
                                    value: o.id,
                                    label: `${o.number} · ${o.counterparty_name || ''}`.trim()
                                }))}
                                loading={ordersLoading}
                                value={orderId}
                                onChange={v => {
                                    setOrderId(v);
                                    setExpected(null);
                                }}
                            />
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                onClick={fetchExpected}
                                loading={expectedLoading}
                                disabled={!report?.rows?.length}
                            >
                                Проверить
                            </Button>
                            <Button
                                onClick={downloadDiscrepancies}
                                disabled={
                                    !selectedRowKeys.length &&
                                    !discrepancyRows.length &&
                                    !comparisonTaskGroups.length
                                }
                                icon={<ExportOutlined />}
                            >
                                Скачать Excel
                                {selectedRowKeys.length
                                    ? ` (${selectedRowKeys.length})`
                                    : expected
                                      ? ` (${
                                            viewMode === 'discrepant_full'
                                                ? comparisonRows.length
                                                : counts.discrepancies
                                        })`
                                      : ''}
                            </Button>
                            <Button
                                icon={<LinkOutlined />}
                                disabled={!orderId}
                                onClick={openCustomerOrder}
                            >
                                Открыть заказ покупателя
                            </Button>
                            <Button
                                type="primary"
                                icon={<CheckOutlined />}
                                disabled={!selectedRowKeys.length}
                                onClick={openAgreeModal}
                            >
                                Согласиться
                            </Button>
                            <Button
                                type="primary"
                                icon={<CheckOutlined />}
                                disabled={!counts.small_side}
                                onClick={openMassAgreeSmallModal}
                            >
                                Мелкие ≤{SMALL_DISCREPANCY_LIMIT} ₽
                                {counts.small_side
                                    ? ` (${counts.small_side})`
                                    : ''}
                            </Button>
                        </Space>

                        {expected?.order ? (
                            <div style={{ marginTop: 10 }}>
                                <Space wrap>
                                    <Text type="secondary">Статус заказа: </Text>
                                    <Tag
                                        color={
                                            expected.order.status === 'closed'
                                                ? 'red'
                                                : 'green'
                                        }
                                    >
                                        {expected.order.status}
                                    </Tag>
                                    <Text>
                                        №{expected.order.number}
                                        {expected.order.counterparty_name
                                            ? ` · ${expected.order.counterparty_name}`
                                            : ''}
                                    </Text>
                                    <Button
                                        type="link"
                                        icon={<LinkOutlined />}
                                        onClick={openCustomerOrder}
                                        style={{ padding: 0 }}
                                    >
                                        Перейти к заказу покупателя
                                    </Button>
                                </Space>
                            </div>
                        ) : null}
                    </Card>
                </div>

                <div style={{ marginTop: 16 }}>
                    <Card title="3) Сверка с отчётом">
                        {expected ? (
                            <div style={{ marginBottom: 12 }}>
                                <Radio.Group
                                    value={viewMode}
                                    onChange={e =>
                                        setViewMode(e.target.value)
                                    }
                                    optionType="button"
                                    buttonStyle="solid"
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 8
                                    }}
                                >
                                    <Radio.Button value="all">
                                        Все {counts.all}
                                    </Radio.Button>
                                    <Radio.Button value="discrepant_full">
                                        Заявки с расх. (полная смета){' '}
                                        {discrepantTaskKeys.size}
                                    </Radio.Button>
                                    <Radio.Button value="matched">
                                        Совпали {counts.matched}
                                    </Radio.Button>
                                    <Radio.Button value="qty_info">
                                        Кол-во Δ · сумма ок{' '}
                                        {counts.qty_info}
                                    </Radio.Button>
                                    <Radio.Button value="mismatch">
                                        Δ сумма {counts.mismatch}
                                    </Radio.Button>
                                    <Radio.Button value="missing_in_report">
                                        Есть в заказе СКС, нет в отчёте Т2{' '}
                                        {counts.missing_in_report}
                                    </Radio.Button>
                                    <Radio.Button value="extra_in_report">
                                        Есть в отчёте Т2, нет в заказе СКС{' '}
                                        {counts.extra_in_report}
                                    </Radio.Button>
                                    <Radio.Button value="small_side">
                                        Мелкие ≤{SMALL_DISCREPANCY_LIMIT} ₽{' '}
                                        {counts.small_side}
                                    </Radio.Button>
                                    <Radio.Button value="sides">
                                        По ТТ напротив{' '}
                                        {counts.missing_in_report +
                                            counts.extra_in_report}
                                    </Radio.Button>
                                </Radio.Group>
                                <Button
                                    type="primary"
                                    icon={<CheckOutlined />}
                                    disabled={!selectedRowKeys.length}
                                    onClick={openAgreeModal}
                                    style={{ marginLeft: 8 }}
                                >
                                    Согласиться
                                    {selectedRowKeys.length
                                        ? ` (${selectedRowKeys.length})`
                                        : ''}
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<CheckOutlined />}
                                    disabled={!counts.small_side}
                                    onClick={openMassAgreeSmallModal}
                                    style={{ marginLeft: 8 }}
                                >
                                    Согласовать мелкие (≤{SMALL_DISCREPANCY_LIMIT} ₽)
                                    {counts.small_side
                                        ? ` (${counts.small_side})`
                                        : ''}
                                </Button>
                                <Input.Search
                                    allowClear
                                    placeholder="Поиск: заявка / БС / ТТ"
                                    style={{
                                        width: 280,
                                        marginLeft: 8,
                                        verticalAlign: 'middle'
                                    }}
                                    value={rowSearch}
                                    onChange={e =>
                                        setRowSearch(e.target.value)
                                    }
                                />
                            </div>
                        ) : null}

                        {expected && viewMode === 'small_side' ? (
                            <Alert
                                type="info"
                                showIcon
                                style={{ marginBottom: 12 }}
                                message={`Мелкие расхождения до ${SMALL_DISCREPANCY_LIMIT} ₽`}
                                description="Сюда попадают: нет с одной стороны и Δ суммы (в т.ч. на копейки), если |Δ| ≤ лимита. Совпадение по сумме при разном кол-ве (qty_info) не входит."
                            />
                        ) : null}

                        {expected && !isSideBySideMode ? (
                            <Alert
                                type="info"
                                showIcon
                                style={{ marginBottom: 12 }}
                                message={
                                    isDiscrepantFullMode
                                        ? 'Полная смета по заявкам с расхождениями'
                                        : 'Группировка по заявкам'
                                }
                                description={
                                    isDiscrepantFullMode
                                        ? 'Показаны все позиции заявки: и совпавшие, и с расхождениями — чтобы сравнить заказ СКС и отчёт Т2 целиком. Внизу каждой заявки — итоги.'
                                        : 'Строки свёрнуты по номеру заявки (ТТ). Раскройте группу, чтобы увидеть позиции.'
                                }
                            />
                        ) : null}

                        {expectedLoading ? (
                            <Text type="secondary">
                                Загрузка ожидаемых данных…
                            </Text>
                        ) : null}

                        {!expected && report ? (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="Нажмите «Проверить», чтобы увидеть сверку"
                            />
                        ) : null}

                        {!comparisonRows.length &&
                        !ttSideGroups.length &&
                        !comparisonTaskGroups.length ? (
                            expected ? (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="Нет строк в этом фильтре"
                                />
                            ) : null
                        ) : !isSideBySideMode ? (
                            <div>
                                {!comparisonTaskGroups.length ? (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description={
                                            isDiscrepantFullMode
                                                ? 'Нет заявок с расхождениями'
                                                : 'Нет строк в этом фильтре'
                                        }
                                    />
                                ) : (
                                    <Collapse
                                        accordion={false}
                                        defaultActiveKey={comparisonTaskGroups
                                            .slice(0, 6)
                                            .map(g => g.key)}
                                        items={comparisonTaskGroups.map(
                                            group => {
                                                const allKeys = group.rows.map(
                                                    r => r.key
                                                );
                                                const selectedCount =
                                                    allKeys.filter(k =>
                                                        selectedRowKeys.includes(
                                                            k
                                                        )
                                                    ).length;
                                                const allSelected =
                                                    allKeys.length > 0 &&
                                                    selectedCount ===
                                                        allKeys.length;
                                                return {
                                                    key: group.key,
                                                    label: (
                                                        <Space wrap>
                                                            <Text strong>
                                                                Заявка{' '}
                                                                {group.ttLabel}
                                                            </Text>
                                                            {group.bsNumber ? (
                                                                <Tag>
                                                                    БС{' '}
                                                                    {
                                                                        group.bsNumber
                                                                    }
                                                                </Tag>
                                                            ) : null}
                                                            <Tag color="green">
                                                                совпало{' '}
                                                                {
                                                                    group.matchedCount
                                                                }
                                                            </Tag>
                                                            {group.qtyInfoCount >
                                                            0 ? (
                                                                <Tag color="blue">
                                                                    кол-во Δ{' '}
                                                                    {
                                                                        group.qtyInfoCount
                                                                    }
                                                                </Tag>
                                                            ) : null}
                                                            <Tag color="orange">
                                                                расхождений{' '}
                                                                {
                                                                    group.mismatchCount
                                                                }
                                                            </Tag>
                                                            <Tag>
                                                                СКС{' '}
                                                                {money(
                                                                    group.sumExpected
                                                                )}{' '}
                                                                ₽
                                                            </Tag>
                                                            <Tag>
                                                                Т2{' '}
                                                                {money(
                                                                    group.sumReport
                                                                )}{' '}
                                                                ₽
                                                            </Tag>
                                                            <Tag
                                                                color={
                                                                    group.sumDelta ===
                                                                    0
                                                                        ? 'default'
                                                                        : 'red'
                                                                }
                                                            >
                                                                Δ{' '}
                                                                {money(
                                                                    group.sumDelta
                                                                )}{' '}
                                                                ₽
                                                            </Tag>
                                                            {group.taskId ||
                                                            group.ttLabel ? (
                                                                <Button
                                                                    type="link"
                                                                    size="small"
                                                                    style={{
                                                                        padding: 0,
                                                                        height:
                                                                            'auto'
                                                                    }}
                                                                    onClick={e => {
                                                                        e.stopPropagation();
                                                                        openTaskModal(
                                                                            {
                                                                                taskId:
                                                                                    group.taskId,
                                                                                claimNumber:
                                                                                    group.ttLabel,
                                                                                taskNumber:
                                                                                    group.ttLabel,
                                                                                bsNumber:
                                                                                    group.bsNumber
                                                                            }
                                                                        );
                                                                    }}
                                                                >
                                                                    Открыть заявку
                                                                </Button>
                                                            ) : null}
                                                            <Checkbox
                                                                checked={
                                                                    allSelected
                                                                }
                                                                indeterminate={
                                                                    selectedCount >
                                                                        0 &&
                                                                    !allSelected
                                                                }
                                                                onClick={e =>
                                                                    e.stopPropagation()
                                                                }
                                                                onChange={e => {
                                                                    const on =
                                                                        e.target
                                                                            .checked;
                                                                    setSelectedRowKeys(
                                                                        prev => {
                                                                            if (
                                                                                on
                                                                            ) {
                                                                                return [
                                                                                    ...new Set(
                                                                                        [
                                                                                            ...prev,
                                                                                            ...allKeys
                                                                                        ]
                                                                                    )
                                                                                ];
                                                                            }
                                                                            return prev.filter(
                                                                                k =>
                                                                                    !allKeys.includes(
                                                                                        k
                                                                                    )
                                                                            );
                                                                        }
                                                                    );
                                                                }}
                                                            >
                                                                Выбрать все
                                                            </Checkbox>
                                                        </Space>
                                                    ),
                                                    children: (
                                                        <Table
                                                            rowKey="key"
                                                            size="small"
                                                            pagination={false}
                                                            columns={mainTable.columns}
                                                            components={resizableTableComponents}
                                                            tableLayout="fixed"
                                                            dataSource={
                                                                group.rows
                                                            }
                                                            scroll={{
                                                                x: mainTable.scrollX
                                                            }}
                                                            rowSelection={{
                                                                selectedRowKeys,
                                                                onChange: keys => {
                                                                    setSelectedRowKeys(
                                                                        prev => {
                                                                            const without =
                                                                                prev.filter(
                                                                                    k =>
                                                                                        !allKeys.includes(
                                                                                            k
                                                                                        )
                                                                                );
                                                                            return [
                                                                                ...without,
                                                                                ...keys
                                                                            ];
                                                                        }
                                                                    );
                                                                },
                                                                getCheckboxProps:
                                                                    record => ({
                                                                        disabled:
                                                                            isSumOkKind(
                                                                                record.kind
                                                                            )
                                                                    })
                                                            }}
                                                            summary={() => (
                                                                <Table.Summary fixed>
                                                                    <Table.Summary.Row>
                                                                        <Table.Summary.Cell
                                                                            index={0}
                                                                            colSpan={2}
                                                                        >
                                                                            <Text strong>
                                                                                Итого по заявке
                                                                            </Text>
                                                                        </Table.Summary.Cell>
                                                                        <Table.Summary.Cell
                                                                            index={2}
                                                                            colSpan={6}
                                                                        />
                                                                        <Table.Summary.Cell
                                                                            index={8}
                                                                            align="right"
                                                                        >
                                                                            <Text strong>
                                                                                {money(
                                                                                    group.sumExpected
                                                                                )}
                                                                            </Text>
                                                                        </Table.Summary.Cell>
                                                                        <Table.Summary.Cell
                                                                            index={9}
                                                                            align="right"
                                                                        >
                                                                            <Text strong>
                                                                                {money(
                                                                                    group.sumReport
                                                                                )}
                                                                            </Text>
                                                                        </Table.Summary.Cell>
                                                                        <Table.Summary.Cell
                                                                            index={10}
                                                                            align="right"
                                                                        >
                                                                            <Text
                                                                                strong
                                                                                type={
                                                                                    group.sumDelta
                                                                                        ? 'danger'
                                                                                        : undefined
                                                                                }
                                                                            >
                                                                                {money(
                                                                                    group.sumDelta
                                                                                )}
                                                                            </Text>
                                                                        </Table.Summary.Cell>
                                                                        <Table.Summary.Cell
                                                                            index={11}
                                                                            colSpan={2}
                                                                        />
                                                                    </Table.Summary.Row>
                                                                </Table.Summary>
                                                            )}
                                                        />
                                                    )
                                                };
                                            }
                                        )}
                                    />
                                )}
                            </div>
                        ) : isSideBySideMode ? (
                            <div>
                                <Alert
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 12 }}
                                    message="Группировка по номеру ТТ"
                                    description="Слева — есть в заказе СКС, нет в отчёте Т2. Справа — есть в отчёте Т2, нет в заказе СКС. Строки одной заявки друг напротив друга."
                                />
                                {!ttSideGroups.length ? (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description="Нет строк для сопоставления"
                                    />
                                ) : (
                                    <Collapse
                                        accordion={false}
                                        defaultActiveKey={ttSideGroups
                                            .slice(0, 8)
                                            .map(g => g.key)}
                                        items={ttSideGroups.map(group => {
                                            const leftKeys = group.left.map(
                                                r => r.key
                                            );
                                            const rightKeys = group.right.map(
                                                r => r.key
                                            );
                                            const allKeys = [
                                                ...leftKeys,
                                                ...rightKeys
                                            ];
                                            const selectedCount =
                                                allKeys.filter(k =>
                                                    selectedRowKeys.includes(k)
                                                ).length;
                                            const allSelected =
                                                allKeys.length > 0 &&
                                                selectedCount ===
                                                    allKeys.length;

                                            return {
                                                key: group.key,
                                                label: (
                                                    <Space wrap>
                                                        <Text strong>
                                                            ТТ{' '}
                                                            {group.ttLabel}
                                                        </Text>
                                                        {group.bsNumber ? (
                                                            <Tag>
                                                                БС{' '}
                                                                {
                                                                    group.bsNumber
                                                                }
                                                            </Tag>
                                                        ) : null}
                                                        <Tag color="red">
                                                            есть в СКС, нет в Т2:{' '}
                                                            {
                                                                group.left
                                                                    .length
                                                            }
                                                        </Tag>
                                                        <Tag color="purple">
                                                            есть в Т2, нет в СКС:{' '}
                                                            {
                                                                group.right
                                                                    .length
                                                            }
                                                        </Tag>
                                                        {group.taskId ||
                                                        group.ttLabel ? (
                                                            <Button
                                                                type="link"
                                                                size="small"
                                                                style={{
                                                                    padding: 0,
                                                                    height:
                                                                        'auto'
                                                                }}
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    openTaskModal(
                                                                        {
                                                                            taskId:
                                                                                group.taskId,
                                                                            claimNumber:
                                                                                group.ttLabel,
                                                                            taskNumber:
                                                                                group.ttLabel,
                                                                            bsNumber:
                                                                                group.bsNumber
                                                                        }
                                                                    );
                                                                }}
                                                            >
                                                                Открыть заявку
                                                            </Button>
                                                        ) : null}
                                                        <Checkbox
                                                            checked={
                                                                allSelected
                                                            }
                                                            indeterminate={
                                                                selectedCount >
                                                                    0 &&
                                                                !allSelected
                                                            }
                                                            onClick={e =>
                                                                e.stopPropagation()
                                                            }
                                                            onChange={e => {
                                                                const on =
                                                                    e.target
                                                                        .checked;
                                                                setSelectedRowKeys(
                                                                    prev => {
                                                                        if (on) {
                                                                            return [
                                                                                ...new Set(
                                                                                    [
                                                                                        ...prev,
                                                                                        ...allKeys
                                                                                    ]
                                                                                )
                                                                            ];
                                                                        }
                                                                        return prev.filter(
                                                                            k =>
                                                                                !allKeys.includes(
                                                                                    k
                                                                                )
                                                                        );
                                                                    }
                                                                );
                                                            }}
                                                        >
                                                            Выбрать все по ТТ
                                                        </Checkbox>
                                                    </Space>
                                                ),
                                                children: (
                                                    <Row gutter={12}>
                                                        <Col span={12}>
                                                            <Card
                                                                size="small"
                                                                title={
                                                                    <Tag color="red">
                                                                        Есть в заказе
                                                                        СКС · нет в
                                                                        отчёте Т2
                                                                    </Tag>
                                                                }
                                                                extra={
                                                                    <Text type="secondary">
                                                                        Итого{' '}
                                                                        {money(
                                                                            group.sumExpected
                                                                        )}{' '}
                                                                        ₽
                                                                    </Text>
                                                                }
                                                            >
                                                                <Table
                                                                    rowKey="key"
                                                                    size="small"
                                                                    pagination={
                                                                        false
                                                                    }
                                                                    columns={
                                                                        sideTable.columns
                                                                    }
                                                                    components={
                                                                        resizableTableComponents
                                                                    }
                                                                    tableLayout="fixed"
                                                                    scroll={{
                                                                        x: sideTable.scrollX
                                                                    }}
                                                                    dataSource={
                                                                        group.left
                                                                    }
                                                                    locale={{
                                                                        emptyText:
                                                                            '—'
                                                                    }}
                                                                    rowSelection={{
                                                                        selectedRowKeys,
                                                                        onChange:
                                                                            keys => {
                                                                                setSelectedRowKeys(
                                                                                    prev => {
                                                                                        const without =
                                                                                            prev.filter(
                                                                                                k =>
                                                                                                    !leftKeys.includes(
                                                                                                        k
                                                                                                    )
                                                                                            );
                                                                                        return [
                                                                                            ...without,
                                                                                            ...keys
                                                                                        ];
                                                                                    }
                                                                                );
                                                                            }
                                                                    }}
                                                                />
                                                            </Card>
                                                        </Col>
                                                        <Col span={12}>
                                                            <Card
                                                                size="small"
                                                                title={
                                                                    <Tag color="purple">
                                                                        Есть в отчёте
                                                                        Т2 · нет в
                                                                        заказе СКС
                                                                    </Tag>
                                                                }
                                                                extra={
                                                                    <Text type="secondary">
                                                                        Итого{' '}
                                                                        {money(
                                                                            group.sumReport
                                                                        )}{' '}
                                                                        ₽
                                                                    </Text>
                                                                }
                                                            >
                                                                <Table
                                                                    rowKey="key"
                                                                    size="small"
                                                                    pagination={
                                                                        false
                                                                    }
                                                                    columns={
                                                                        sideTable.columns
                                                                    }
                                                                    components={
                                                                        resizableTableComponents
                                                                    }
                                                                    tableLayout="fixed"
                                                                    scroll={{
                                                                        x: sideTable.scrollX
                                                                    }}
                                                                    dataSource={
                                                                        group.right
                                                                    }
                                                                    locale={{
                                                                        emptyText:
                                                                            '—'
                                                                    }}
                                                                    rowSelection={{
                                                                        selectedRowKeys,
                                                                        onChange:
                                                                            keys => {
                                                                                setSelectedRowKeys(
                                                                                    prev => {
                                                                                        const without =
                                                                                            prev.filter(
                                                                                                k =>
                                                                                                    !rightKeys.includes(
                                                                                                        k
                                                                                                    )
                                                                                            );
                                                                                        return [
                                                                                            ...without,
                                                                                            ...keys
                                                                                        ];
                                                                                    }
                                                                                );
                                                                            }
                                                                    }}
                                                                />
                                                            </Card>
                                                        </Col>
                                                    </Row>
                                                )
                                            };
                                        })}
                                    />
                                )}
                            </div>
                        ) : null}
                    </Card>
                </div>
            </div>

            <Modal
                open={agreeOpen}
                width={860}
                title={
                    agreeTargetRows?.length
                        ? `Согласовать мелкие (≤${SMALL_DISCREPANCY_LIMIT} ₽) · ${agreeTargetRows.length}`
                        : 'Согласиться с выбранными строками'
                }
                onCancel={() => {
                    setAgreeOpen(false);
                    setApplyFeedback(null);
                    setAgreeTargetRows(null);
                }}
                footer={
                    <Space>
                        <Button
                            onClick={() => {
                                setAgreeOpen(false);
                                setApplyFeedback(null);
                                setAgreeTargetRows(null);
                            }}
                            disabled={agreeApplying}
                        >
                            Закрыть
                        </Button>
                        <Button
                            type="primary"
                            danger
                            icon={<CheckOutlined />}
                            loading={agreeApplying}
                            disabled={!rowsForAgree.length}
                            onClick={applyAgreement}
                        >
                            Применить корректировки в ВДР
                        </Button>
                    </Space>
                }
            >
                {applyFeedback?.lines?.length ? (
                    <Alert
                        type="error"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message="Ошибка применения"
                        description={
                            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                                {applyFeedback.lines.map((line, index) => (
                                    <li key={`${index}-${line.slice(0, 24)}`}>
                                        {line}
                                    </li>
                                ))}
                            </ul>
                        }
                    />
                ) : null}
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={
                        <>
                            Затронет заказ покупателя{' '}
                            <Text strong>
                                №{expected?.order?.number || orderId || '—'}
                            </Text>
                            {expected?.order?.counterparty_name
                                ? ` · ${expected.order.counterparty_name}`
                                : ''}
                        </>
                    }
                    description="После подтверждения доходные строки ВДР будут приведены к отчёту. В истории задач появится запись с пометкой «Сверка заказа покупателя»."
                />
                <Text>
                    Заказ покупателя:{' '}
                    <Text strong>
                        №{expected?.order?.number || orderId || '—'}
                    </Text>
                    {expected?.order?.counterparty_name
                        ? ` · ${expected.order.counterparty_name}`
                        : ''}
                </Text>
                <div style={{ marginTop: 16 }}>
                    {[
                        [
                            'mismatch',
                            'Изменение',
                            'Будут изменены доходные строки ВДР (кол-во под сумму отчёта, цена без изменений)'
                        ],
                        [
                            'extra_in_report',
                            'Добавление',
                            'Будут добавлены доходные строки ВДР и привязаны к заказу'
                        ],
                        [
                            'missing_in_report',
                            'Обнуление',
                            'Строки ВДР, которых нет в отчёте, будут обнулены (кол-во → 0)'
                        ],
                        [
                            'matched',
                            'Без изменений',
                            'Строки совпали, менять ничего не нужно'
                        ]
                    ].map(([kind, title, hint]) => {
                        const rows = rowsForAgree.filter(r => r.kind === kind);
                        if (!rows.length) return null;
                        return (
                            <Card
                                key={kind}
                                size="small"
                                title={`${title} · ${rows.length}`}
                                style={{ marginBottom: 12 }}
                            >
                                <Text type="secondary">{hint}</Text>
                                <div style={{ marginTop: 8 }}>
                                    {rows.map(row => {
                                        const lines = (
                                            row.invoiceLines || []
                                        ).filter(
                                            (line, idx, arr) =>
                                                line.id
                                                    ? arr.findIndex(
                                                          x => x.id === line.id
                                                      ) === idx
                                                    : true
                                        );
                                        return (
                                            <div
                                                key={row.key}
                                                style={{
                                                    marginTop: 10,
                                                    paddingTop: 10,
                                                    borderTop:
                                                        '1px solid #f0f0f0'
                                                }}
                                            >
                                                <Text strong>
                                                    ТТ {row.taskNumber || '—'}
                                                </Text>
                                                {' · '}
                                                пункт {row.workCode || '—'}
                                                {row.workName
                                                    ? ` · ${row.workName}`
                                                    : ''}
                                                <div>
                                                    <Text type="secondary">
                                                        Заказ: кол-во{' '}
                                                        {row.expectedQty}, сумма{' '}
                                                        {money(
                                                            row.expectedAmount
                                                        )}
                                                        {' · '}
                                                        Отчёт: кол-во{' '}
                                                        {row.reportQty}, сумма{' '}
                                                        {money(row.reportAmount)}
                                                    </Text>
                                                </div>
                                                {lines.length ? (
                                                    <div style={{ marginTop: 4 }}>
                                                        <Text type="secondary">
                                                            Строки ВДР:{' '}
                                                            {lines
                                                                .map(
                                                                    line =>
                                                                        `id ${line.id} «${line.rate_name || '—'}» (${line.quantity} / ${money(line.amount)})`
                                                                )
                                                                .join('; ')}
                                                        </Text>
                                                    </div>
                                                ) : kind ===
                                                  'extra_in_report' ? (
                                                    <div style={{ marginTop: 4 }}>
                                                        <Text type="secondary">
                                                            Новых строк ВДР в
                                                            заказе пока нет —
                                                            их добавят из отчёта
                                                        </Text>
                                                    </div>
                                                ) : null}
                                            </div>
                                        );
                                    })}
                                </div>
                            </Card>
                        );
                    })}
                </div>
            </Modal>

            <Modal
                open={taskModalOpen}
                width="94vw"
                style={{ top: 20 }}
                title={
                    <Space wrap>
                        <span>
                            Заявка №
                            {modalTask.external_id ||
                                modalTask.nomer_tt ||
                                '—'}
                        </span>
                        {modalTask.status ? (
                            <Tag>{modalTask.status}</Tag>
                        ) : null}
                    </Space>
                }
                footer={
                    <Space>
                        <Button
                            icon={<LinkOutlined />}
                            disabled={!orderId}
                            onClick={openCustomerOrder}
                        >
                            Перейти к заказу покупателя
                        </Button>
                        <Button onClick={closeTaskModal}>Закрыть</Button>
                    </Space>
                }
                onCancel={closeTaskModal}
                destroyOnClose
                styles={{
                    body: {
                        maxHeight: 'calc(100vh - 140px)',
                        overflowY: 'auto'
                    }
                }}
            >
                <Spin spinning={taskModalLoading}>
                    <Row gutter={[16, 16]}>
                        <Col span={16}>
                            <Card size="small" title="Информация о заявке">
                                <Descriptions
                                    bordered
                                    column={2}
                                    size="small"
                                >
                                    <Descriptions.Item label="ID">
                                        {modalTask.external_id || '—'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Исполнитель">
                                        {modalTask.assigned_user_fio || '—'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Статус">
                                        {gepardTask.status_name ||
                                            gepardTask.status?.name ||
                                            modalTask.status ||
                                            '—'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Вид работ">
                                        {modalTask.news_type_name ||
                                            gepardTask.news_type_name ||
                                            gepardTask.type?.name ||
                                            '—'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Номер ТТ">
                                        {modalTask.nomer_tt || '—'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Номер БС">
                                        {modalTask.bs_number || '—'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Осметил">
                                        {modalTask.filling_user_name ||
                                            gepardTask.filling_user_fio ||
                                            gepardTask.filling_user_name ||
                                            '—'}
                                    </Descriptions.Item>
                                    <Descriptions.Item
                                        label="Название"
                                        span={2}
                                    >
                                        {modalTask.title ||
                                            gepardTask.title ||
                                            '—'}
                                    </Descriptions.Item>
                                </Descriptions>
                            </Card>

                            <Card
                                size="small"
                                title="Отчёт исполнителя"
                                style={{ marginTop: 16 }}
                            >
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Text strong>
                                            Описание выполненных работ
                                        </Text>
                                        <div
                                            style={{
                                                padding: 8,
                                                background: '#fffbe6',
                                                border: '1px solid #ffe58f',
                                                borderRadius: 4,
                                                marginTop: 5,
                                                minHeight: 100,
                                                whiteSpace: 'pre-wrap'
                                            }}
                                        >
                                            {modalTask.work_done_description ||
                                                'Нет данных'}
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <Text strong>
                                            Комментарии исполнителя (не для
                                            заказчика)
                                        </Text>
                                        <div
                                            style={{
                                                padding: 8,
                                                background: '#fffbe6',
                                                border: '1px solid #ffe58f',
                                                borderRadius: 4,
                                                marginTop: 5,
                                                minHeight: 100,
                                                whiteSpace: 'pre-wrap'
                                            }}
                                        >
                                            {modalTask.executor_comments ||
                                                'Нет данных'}
                                        </div>
                                    </Col>
                                </Row>
                            </Card>

                            <Card
                                size="small"
                                title="Сверка смет: заказ СКС ↔ отчёт Т2"
                                style={{ marginTop: 16 }}
                                extra={
                                    taskCompareRows.length ? (
                                        <Space wrap>
                                            <Tag>
                                                СКС {money(taskCompareTotals.expected)} ₽
                                            </Tag>
                                            <Tag>
                                                Т2 {money(taskCompareTotals.report)} ₽
                                            </Tag>
                                            <Tag
                                                color={
                                                    taskCompareTotals.delta
                                                        ? 'red'
                                                        : 'green'
                                                }
                                            >
                                                Δ {money(taskCompareTotals.delta)} ₽
                                            </Tag>
                                        </Space>
                                    ) : null
                                }
                            >
                                {!taskCompareRows.length ? (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description="Нет строк сверки по этой заявке"
                                    />
                                ) : (
                                    <Table
                                        rowKey="key"
                                        size="small"
                                        pagination={false}
                                        scroll={{ x: taskCompareTable.scrollX }}
                                        tableLayout="fixed"
                                        components={resizableTableComponents}
                                        dataSource={taskCompareRows}
                                        columns={taskCompareTable.columns}
                                        summary={() => (
                                            <Table.Summary.Row>
                                                <Table.Summary.Cell
                                                    index={0}
                                                    colSpan={5}
                                                >
                                                    <Text strong>
                                                        Итого по заявке
                                                    </Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell
                                                    index={5}
                                                    align="right"
                                                >
                                                    <Text strong>
                                                        {money(
                                                            taskCompareTotals.expected
                                                        )}
                                                    </Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell
                                                    index={6}
                                                    align="right"
                                                >
                                                    <Text strong>
                                                        {money(
                                                            taskCompareTotals.report
                                                        )}
                                                    </Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell
                                                    index={7}
                                                    align="right"
                                                >
                                                    <Text strong>
                                                        {money(
                                                            taskCompareTotals.delta
                                                        )}
                                                    </Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={8} />
                                            </Table.Summary.Row>
                                        )}
                                    />
                                )}
                            </Card>

                            <Card
                                size="small"
                                title="Осмечивание ВДР"
                                style={{ marginTop: 16 }}
                            >
                                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                                    <Col span={12}>
                                        <Statistic
                                            title="Доходных строк"
                                            value={incomeInvoices.length}
                                        />
                                    </Col>
                                    <Col span={12}>
                                        <Statistic
                                            title="Итого"
                                            value={incomeTotal}
                                            precision={2}
                                            suffix="₽"
                                        />
                                    </Col>
                                </Row>
                                <Table
                                    rowKey="id"
                                    columns={incomeTable.columns}
                                    components={resizableTableComponents}
                                    tableLayout="fixed"
                                    dataSource={incomeInvoices}
                                    pagination={false}
                                    size="small"
                                    scroll={{ x: incomeTable.scrollX }}
                                    locale={{
                                        emptyText: 'Доходная ВДР отсутствует'
                                    }}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card
                                size="small"
                                title={`Фотографии (${taskPhotos.length})`}
                            >
                                {taskPhotos.length ? (
                                    <Image.PreviewGroup>
                                        <div className="customer-task-gallery">
                                            {taskPhotos.map(photo => (
                                                <div
                                                    key={photo.num}
                                                    className="customer-task-gallery-item"
                                                >
                                                    {taskPhotoUrls[photo.num] ? (
                                                        <Image
                                                            src={
                                                                taskPhotoUrls[
                                                                    photo.num
                                                                ]
                                                            }
                                                            preview={{
                                                                src:
                                                                    taskOriginalUrls[
                                                                        photo.num
                                                                    ] ||
                                                                    taskPhotoUrls[
                                                                        photo.num
                                                                    ]
                                                            }}
                                                            alt={
                                                                photo.description ||
                                                                `Фото ${photo.num}`
                                                            }
                                                            className="customer-task-gallery-image"
                                                        />
                                                    ) : (
                                                        <div className="customer-task-gallery-loading">
                                                            <Spin />
                                                        </div>
                                                    )}
                                                    <div className="customer-task-gallery-caption">
                                                        <Text strong>
                                                            Фото №{photo.num}
                                                        </Text>
                                                        {photo.description ? (
                                                            <Text type="secondary">
                                                                {photo.description}
                                                            </Text>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </Image.PreviewGroup>
                                ) : (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description="Фотографии отсутствуют"
                                    />
                                )}
                            </Card>
                        </Col>
                    </Row>
                </Spin>
            </Modal>
        </Layout>
    );
};

export default CustomerOrderComparePage;
